import React, { useEffect, useState } from "react";
import axios from "axios";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";

const CARDS = [
  {
    key: "totalRevenueThisMonth",
    label: "Revenue",
    sub: "All successful charges",
    icon: AttachMoneyIcon,
    format: (v) => `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
  },
  {
    key: "activeSubscriptionsCount",
    label: "Active Subscriptions",
    sub: "Currently active plans",
    icon: AutorenewIcon,
    format: (v) => v.toLocaleString(),
  },
  {
    key: "mrr",
    label: "MRR",
    sub: "Monthly recurring revenue",
    icon: ShowChartIcon,
    format: (v) => `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
  },
  {
    key: "churnRate",
    label: "Churn Rate",
    sub: "Canceled vs total this month",
    icon: TrendingDownIcon,
    format: (v) => `${v}%`,
  },
];

function SkeletonCard() {
  return (
    <div style={cardBase}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div className="stripe-skeleton" style={{ width: "55%", height: 11, borderRadius: 4, marginBottom: 14 }} />
          <div className="stripe-skeleton" style={{ width: "70%", height: 30, borderRadius: 6, marginBottom: 10 }} />
          <div className="stripe-skeleton" style={{ width: "45%", height: 11, borderRadius: 4 }} />
        </div>
        <div className="stripe-skeleton" style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0, marginLeft: 12 }} />
      </div>
    </div>
  );
}

const cardBase = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 14,
  padding: "22px 24px",
};

const toUnix = (d, endOfDay) => {
  if (!d) return null;
  const dt = new Date(d);
  if (endOfDay) dt.setHours(23, 59, 59, 999);
  return Math.floor(dt.getTime() / 1000);
};

export default function SummaryCards({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    let url = "/api/stripe/revenue/summary";
    const s = toUnix(startDate, false);
    const e = toUnix(endDate, true);
    const params = [];
    if (s) params.push(`startDate=${s}`);
    if (e) params.push(`endDate=${e}`);
    if (params.length) url += `?${params.join("&")}`;

    axios
      .get(url)
      .then((res) => {
        if (res.data.success) setData(res.data.data);
        else setError(true);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading) {
    return (
      <div className="stripe-stat-grid">
        {CARDS.map((c) => <SkeletonCard key={c.key} />)}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...cardBase, gridColumn: "1 / -1" }}>
        <p style={{ margin: 0, fontFamily: "Nunito", color: "#ef4444", fontWeight: 700, fontSize: 14 }}>
          Failed to load summary — check your Stripe secret key configuration.
        </p>
      </div>
    );
  }

  return (
    <div className="stripe-stat-grid">
      {CARDS.map((card) => {
        const value = data ? data[card.key] : null;
        const isChurn = card.key === "churnRate";
        const churnHigh = isChurn && value > 5;
        const Icon = card.icon;

        // Only churn rate gets a status colour — everything else is black
        const churnColor = churnHigh ? "#dc2626" : "#16a34a";

        return (
          <div key={card.key} style={cardBase}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Label */}
                <p style={{
                  margin: "0 0 10px",
                  fontFamily: "Nunito",
                  fontSize: 11,
                  fontWeight: 800,
                  color: "#9ca3af",
                  textTransform: "uppercase",
                  letterSpacing: "0.6px",
                }}>
                  {card.label}
                </p>

                {/* Value */}
                <p style={{
                  margin: "0 0 8px",
                  fontFamily: "Nunito",
                  fontSize: 28,
                  fontWeight: 800,
                  color: isChurn ? churnColor : "#111827",
                  letterSpacing: "-0.5px",
                  lineHeight: 1.1,
                }}>
                  {value !== null ? card.format(value) : "—"}
                  {/* Status badge only on churn */}
                  {isChurn && value !== null && (
                    <span style={{
                      marginLeft: 10,
                      fontSize: 11,
                      fontWeight: 800,
                      padding: "3px 8px",
                      borderRadius: 20,
                      background: churnHigh ? "#fef2f2" : "#f0fdf4",
                      color: churnColor,
                      verticalAlign: "middle",
                    }}>
                      {churnHigh ? "High" : "Low"}
                    </span>
                  )}
                </p>

                {/* Subtitle */}
                <p style={{
                  margin: 0,
                  fontFamily: "Nunito",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#d1d5db",
                }}>
                  {(card.key === "totalRevenueThisMonth" || card.key === "churnRate") && (startDate || endDate)
                    ? "Selected date range"
                    : card.sub}
                </p>
              </div>

              {/* Icon — muted, right-aligned */}
              <div style={{
                flexShrink: 0,
                marginLeft: 16,
                width: 34,
                height: 34,
                borderRadius: 9,
                background: "#f9fafb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#d1d5db",
              }}>
                <Icon style={{ fontSize: 18 }} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
